import type { TileColumns } from '@adweb/shared';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

import { ActionsMenu, type ActionsMenuItem } from '../../components/ui/ActionsMenu';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { triggerTypeMeta } from '../../components/ui/TriggerTypeBadge';

import type { AutomationSummary } from './automation-summary';
import { AutomationActionButton } from './AutomationActionButton';
import { FavoriteStar } from './favorites';

// Classes complètes (Tailwind ne génère que les classes écrites en toutes lettres).
const GRID: Record<TileColumns, string> = {
  3: 'gap-4 sm:grid-cols-2 lg:grid-cols-3',
  4: 'gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  5: 'gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5',
};

/** Vue « tuiles » : une carte par automatisation avec un grand bouton d'action. */
export function AutomationTiles({
  automations,
  showOrganization,
  onRun,
  columns = 3,
  favorites,
  onToggleFavorite,
  actions,
}: {
  automations: AutomationSummary[];
  showOrganization: boolean;
  onRun: (a: AutomationSummary) => void;
  /** Automatisations épinglées et bascule (étoile) ; sans elles, pas d'étoile. */
  favorites?: Set<string>;
  onToggleFavorite?: (id: string) => void;
  /** Actions secondaires (Désactiver, Modifier, Supprimer) selon le rôle. */
  actions?: (a: AutomationSummary) => ActionsMenuItem[];
  /** Tuiles par ligne sur grand écran (préférence de l'utilisateur). */
  columns?: TileColumns;
}) {
  return (
    <ul className={clsx('grid grid-cols-1', GRID[columns])}>
      {automations.map((a) => {
        const meta = triggerTypeMeta(a.triggerType);
        const Icon = meta.icon;
        return (
          <li
            key={a.id}
            className={clsx('surface flex flex-col gap-3', columns === 5 ? 'p-4' : 'p-5')}
          >
            <div className="flex items-start justify-between gap-3">
              <span
                className={clsx('grid h-10 w-10 shrink-0 place-items-center rounded-xl', meta.cls)}
                title={meta.label}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="flex items-center gap-1">
                {a.status !== 'ACTIVE' && <StatusBadge status={a.status} />}
                {favorites && onToggleFavorite && (
                  <FavoriteStar
                    automation={a}
                    favorite={favorites.has(a.id)}
                    onToggle={onToggleFavorite}
                  />
                )}
                {actions && (
                  <ActionsMenu label={`Autres actions pour ${a.name}`} items={actions(a)} />
                )}
              </div>
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-slate-900">{a.name}</h3>
              {showOrganization && a.organization && (
                <p className="text-xs text-slate-500">{a.organization.name}</p>
              )}
              <p className="mt-1.5 line-clamp-3 text-xs leading-relaxed text-slate-600">
                {a.description ?? `${meta.label}${a.category ? ` · ${a.category}` : ''}`}
              </p>
            </div>
            <div className="mt-auto flex flex-col gap-3">
              <AutomationActionButton automation={a} onRun={onRun} size="block" />
              <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
                <span>
                  {a.estimatedMinutesSavedPerRun != null
                    ? `${a.estimatedMinutesSavedPerRun} min gagnées / exécution`
                    : meta.label}
                </span>
                <Link
                  to={`/automations/${a.id}`}
                  className="shrink-0 whitespace-nowrap font-medium text-brand hover:underline"
                >
                  Détails →
                </Link>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
